import { type FormikHelpers } from 'formik';
import { sendFeedback, uploadFeedbackFiles } from '@common/api/rest';
import { isFileAllowed, isFileSizeValid } from '@common/utils/validation/validate';

export interface FormValues {
  topic: string;
  description: string;
  uploadedFiles: File[];
  required?: any;
}

export const maximumFileSize = 26214400;
export const allAllowedFileExtensions = [
  '.png',
  '.jpeg',
  '.jpg',
  '.pjpeg',
  '.bmp',
  '.apng',
  '.gif',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.rtf',
  '.pdf',
  '.gz',
  '.zip',
  '.txt',
];
export const endUserAllowedFileExtensions = ['.pdf', '.jpg', '.png'];

export const initialValues = {
  topic: '',
  description: '',
  uploadedFiles: [] as File[],
};

export const getAllowedFileExtensions = (isCustomer: boolean) =>
  (isCustomer ? endUserAllowedFileExtensions : allAllowedFileExtensions).join(', ');

export const validate = (
  values: FormValues,
  isCustomer: boolean,
): Partial<FormValues> => {
  const errors: Partial<FormValues> = {};
  if (!values.topic) {
    errors.topic = 'common.errors.required';
  }
  if (!values.description) {
    errors.description = 'common.errors.required';
  }
  if (!values.topic || !values.description) {
    errors.required = 'common.errors.required-general';
  } else if (values.uploadedFiles?.length > 0) {
    const { uploadedFiles } = values;
    const allowedFileExtensions = getAllowedFileExtensions(isCustomer);
    if (
      !uploadedFiles.every((file: File) => isFileSizeValid(file, maximumFileSize))
      || !uploadedFiles.every((file: File) => isFileAllowed(file, allowedFileExtensions))
    ) {
      errors.required = 'common.errors.review-general';
    }
  }

  return errors;
};

export const submit = async (
  values: FormValues,
  { setStatus }: FormikHelpers<FormValues>,
): Promise<void> => {
  let feedbackSent = false;
  if (values.uploadedFiles.length > 0) {
    const uploadedFiles = await uploadFeedbackFiles(values.uploadedFiles);

    if (uploadedFiles) {
      feedbackSent = await sendFeedback(
        values.topic,
        values.description,
        uploadedFiles,
      );
    } else {
      setStatus({
        error: 'feedback.popup.error',
        success: false,
      });
    }
  } else {
    feedbackSent = await sendFeedback(values.topic, values.description);
  }

  if (feedbackSent) {
    setStatus({
      success: true,
      error: undefined,
    });
  } else {
    setStatus({
      error: 'feedback.popup.error',
      success: false,
    });
  }
};
